"use client"

import Link from "next/link"
import { authClient } from "@/lib/auth-client"
import FadeInView from "./animate-ui/fade-in-view"
import { hero, statsIntro, stats } from "@/content/landing"

// Couleur du CTA violet : légèrement plus foncée que l'accent de marque
// (#7F77DD) utilisé ailleurs sur la page (badges, icônes, dégradé du H1).
// Nécessaire uniquement ici car ce ton porte du texte blanc en dessous de
// 18.66px/bold : #7F77DD donne un contraste de 3.76:1 (sous le seuil AA
// 4.5:1 pour du texte normal), #655BCF passe à 5.32:1.
const CTA_BG = "#655BCF"

export default function HeroSection() {
  const { data: session } = authClient.useSession()
  const appHref = session ? "/home" : "/sign-up"
  return (
    <section className="relative pt-16 pb-24 md:pt-24 md:pb-32">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">

          {/* ── Texte ── */}
          <div className="flex-1 text-center lg:text-left max-w-2xl mx-auto lg:mx-0">

            {/* Mascotte, visible uniquement en mobile ici (au-dessus du H1) —
                réapparaît à côté du mockup téléphone sur desktop, cf. plus bas. */}
            <FadeInView className="flex justify-center md:hidden mb-6">
              <FoxMascot size={120} />
            </FadeInView>

            <FadeInView>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "rgba(127,119,221,0.15)", border: "1px solid rgba(127,119,221,0.3)",
                borderRadius: 999, padding: "5px 14px", fontSize: 13, fontWeight: 600,
                color: "#a09ae8", marginBottom: 24,
              }}>
                <i className="ti ti-school" style={{ fontSize: 15 }} />
                {hero.badge}
              </span>
            </FadeInView>

            <FadeInView delay={0.1}>
              <h1 style={{
                fontSize: "clamp(2.4rem, 6vw, 4rem)",
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                marginBottom: 20,
                color: "var(--foreground)",
              }}>
                {hero.titleLine1}
                <br />
                <span style={{
                  backgroundImage: "linear-gradient(135deg, #7F77DD 0%, #b8b2f0 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}>
                  {hero.titleLine2}
                </span>
              </h1>
            </FadeInView>

            <FadeInView delay={0.2}>
              <p style={{
                fontSize: "1.1rem", lineHeight: 1.7,
                color: "var(--muted-foreground)", marginBottom: 32, maxWidth: 480,
              }}>
                {hero.subtitle}
              </p>
            </FadeInView>

            <FadeInView delay={0.3}>
              {/* Un seul CTA par écran mobile dans le hero : "Se connecter"
                  vit désormais uniquement dans le header. */}
              <div style={{ display: "flex", justifyContent: "center" }} className="lg:justify-start">
                <Link href={appHref} style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: CTA_BG, color: "#fff",
                  padding: "12px 24px", borderRadius: 10, fontWeight: 700,
                  fontSize: 15, textDecoration: "none",
                  boxShadow: "0 4px 24px rgba(127,119,221,0.4)",
                  transition: "opacity .15s",
                }}>
                  <i className={`ti ${session ? "ti-layout-dashboard" : "ti-rocket"}`} style={{ fontSize: 18 }} />
                  {session ? hero.ctaConnecte : hero.ctaAnonyme}
                </Link>
              </div>
              {!session && (
                <p style={{
                  fontSize: 12.5, color: "var(--muted-foreground)", opacity: 0.8,
                  marginTop: 10, textAlign: "center",
                }} className="lg:text-left">
                  {hero.reassurance}
                </p>
              )}
            </FadeInView>

            {/* Stats */}
            <FadeInView delay={0.45}>
              <p style={{
                fontSize: 13, color: "var(--muted-foreground)", opacity: 0.85,
                marginTop: 40, marginBottom: 12, textAlign: "center",
              }} className="lg:text-left">
                {statsIntro}
              </p>
              <div style={{
                display: "flex", flexWrap: "wrap", gap: "20px 32px",
                justifyContent: "center",
              }} className="lg:justify-start">
                {stats.map((s) => (
                  <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <i className={`ti ${s.icon}`} style={{ fontSize: 18, color: "#7F77DD" }} aria-hidden="true" />
                    <span style={{ fontWeight: 700, color: "var(--foreground)", fontSize: 15 }}>{s.value}</span>
                    <span style={{ color: "var(--muted-foreground)", fontSize: 14 }}>{s.label}</span>
                  </div>
                ))}
              </div>
            </FadeInView>
          </div>

          {/* ── Mockup téléphone + mascotte (desktop uniquement) ── */}
          <FadeInView delay={0.2} className="flex-shrink-0 hidden md:flex items-end gap-4">
            <FoxMascot size={140} className="mb-4" />
            <AppMockup />
          </FadeInView>

        </div>
      </div>
    </section>
  )
}

// Illustration mascotte — purement décorative (aria-hidden), jamais seule
// porteuse d'information. Asset attendu : public/mascotte-renard.png
// (fourni séparément, cf. échange avec l'équipe produit).
function FoxMascot({ size, className }: { size: number; className?: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src="/mascotte-renard.png"
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      className={className}
      style={{ width: size, height: "auto", objectFit: "contain" }}
    />
  )
}

function AppMockup() {
  return (
    <div style={{
      width: 260,
      borderRadius: 32,
      border: "2px solid rgba(127,119,221,0.25)",
      background: "#141420",
      boxShadow: "0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(127,119,221,0.1)",
      overflow: "hidden",
      userSelect: "none",
    }} aria-hidden="true">

      {/* Header violet */}
      <div style={{
        background: "linear-gradient(135deg, #7F77DD 0%, #5a54b0 100%)",
        padding: "18px 16px 14px",
      }}>
        <p style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", margin: "0 0 2px" }}>Bonjour, Léa 👋</p>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Tableau de bord</span>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: "rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700, color: "#fff",
          }}>L</div>
        </div>
        {/* XP bar */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>Niv. 3</span>
          <div style={{ flex: 1, height: 5, background: "rgba(255,255,255,0.2)", borderRadius: 99 }}>
            <div style={{ width: "62%", height: "100%", background: "#fff", borderRadius: 99 }} />
          </div>
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>260 XP</span>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "12px 12px 8px" }}>

        {/* Flashcard */}
        <div style={{
          background: "#22223a",
          borderRadius: 12,
          border: "1px solid rgba(127,119,221,0.2)",
          padding: "16px 14px",
          marginBottom: 10,
          textAlign: "center",
        }}>
          <p style={{ fontSize: 10, color: "#9896b8", margin: "0 0 8px" }}>Révision du jour · 1/6</p>
          <div style={{
            background: "rgba(127,119,221,0.12)", borderRadius: 8,
            padding: "14px 10px", marginBottom: 10,
          }}>
            <p style={{ fontSize: 16, fontWeight: 800, color: "#e8e5f8", margin: 0 }}>Mimesis</p>
            <p style={{ fontSize: 10, color: "#a8a5c8", margin: "4px 0 0" }}>L&apos;Art · Philosophie</p>
          </div>
          <p style={{ fontSize: 10, color: "#9896b8", margin: 0 }}>Appuie pour voir la définition →</p>
        </div>

        {/* Quality buttons */}
        <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
          {[
            { label: "Oublié", color: "#e53e3e", bg: "rgba(229,62,62,0.12)" },
            { label: "Bien", color: "#1D9E75", bg: "rgba(29,158,117,0.12)" },
            { label: "Parfait", color: "#7F77DD", bg: "rgba(127,119,221,0.12)" },
          ].map((b) => (
            <div key={b.label} style={{
              flex: 1, textAlign: "center", padding: "6px 2px",
              borderRadius: 7, background: b.bg, border: `1px solid ${b.color}40`,
            }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: b.color }}>{b.label}</span>
            </div>
          ))}
        </div>

        {/* Stat mini */}
        <div style={{ display: "flex", gap: 5 }}>
          {[
            { icon: "ti-flame", val: "5", color: "#f97316" },
            { icon: "ti-cards", val: "12", color: "#7F77DD" },
            { icon: "ti-rosette-discount-check", val: "87%", color: "#1D9E75" },
          ].map((s) => (
            <div key={s.icon} style={{
              flex: 1, background: "#1b1b2c", borderRadius: 8,
              padding: "7px 4px", textAlign: "center",
            }}>
              <i className={`ti ${s.icon}`} style={{ fontSize: 14, color: s.color, display: "block" }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: "#e8e5f8" }}>{s.val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom nav */}
      <div style={{
        display: "flex", justifyContent: "space-around",
        padding: "8px 0 10px",
        borderTop: "1px solid rgba(127,119,221,0.15)",
        background: "#22223a",
      }}>
        {[
          { icon: "ti-home-2", active: false },
          { icon: "ti-book-2", active: false },
          { icon: "ti-cards", active: true },
          { icon: "ti-chart-bar", active: false },
        ].map((n) => (
          <i key={n.icon} className={`ti ${n.icon}`} style={{
            fontSize: 20,
            color: n.active ? "#9490e8" : "#4a4870",
          }} />
        ))}
      </div>
    </div>
  )
}
